<template>
  <div class="user-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('user.management') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button 
              type="primary" 
              @click="showCreateModal = true"
              v-if="userStore.isAdmin || userStore.canManageUsers"
            >
              <template #icon>
                <PlusOutlined />
              </template>
              {{ $t('user.createUser') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>
    
    <!-- 统计卡片 -->
    <a-row :gutter="16" class="stats-row">
      <a-col :span="8">
        <a-card>
          <a-statistic :title="$t('user.totalUsers')" :value="stats.total_users" />
        </a-card>
      </a-col>
      <a-col :span="8">
        <a-card>
          <a-statistic :title="$t('user.activeUsers')" :value="stats.active_users" />
        </a-card>
      </a-col>
      <a-col :span="8">
        <a-card>
          <a-statistic :title="$t('user.admin')" :value="stats.admin_users" />
        </a-card>
      </a-col>
    </a-row>
    
    <!-- 搜索和筛选 -->
    <a-card class="search-card">
      <a-form layout="inline" :model="searchForm" @finish="handleSearch">
        <a-form-item :label="$t('common.search')">
          <a-input
            v-model:value="searchForm.search"
            :placeholder="$t('user.searchPlaceholder')"
            style="width: 200px"
            allow-clear
          />
        </a-form-item>
        <a-form-item :label="$t('common.status')">
          <a-select
            v-model:value="searchForm.status"
            :placeholder="$t('user.selectStatus')"
            style="width: 120px"
            allow-clear
          >
            <a-select-option value="active">{{ $t('user.active') }}</a-select-option>
            <a-select-option value="inactive">{{ $t('user.inactive') }}</a-select-option>
            <a-select-option value="suspended">{{ $t('user.suspended') }}</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" html-type="submit" :loading="loading">
              {{ $t('common.search') }}
            </a-button>
            <a-button @click="handleReset">{{ $t('user.reset') }}</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>


    
    <!-- 用户列表 -->
    <a-card>
      <DataTable
        :columns="columns"
        :data-source="users"
        :loading="loading"
        :pagination="pagination"
        :actions="actions"
        row-key="id"
        @change="handleTableChange"
        @action="handleAction"
      >
        <template #roles="{ record }">
          <template v-if="record.roles?.length">
            <a-tag v-for="r in record.roles" :key="r.id" class="mr-1">
              {{ r.name }}
            </a-tag>
          </template>
          <span v-else>-</span>
        </template>
        
        <template #status="{ text }">
          <StatusTag :status="text" type="user" />
        </template>
        
        <template #language="{ text }">
          <LanguageTag v-if="text" :code="text" />
          <span v-else>-</span>
        </template>
        
        <template #created_at="{ text }">
          {{ formatDateTime(text) }}
        </template>
      </DataTable>
    </a-card>
    
    <!-- 创建用户模态框 -->
    <CreateUserModal
      v-model:visible="showCreateModal"
      @success="handleCreateSuccess"
    />
    
    <!-- 编辑用户模态框 -->
    <EditUserModal
      v-model:visible="showEditModal"
      :user="currentUser"
      @success="handleEditSuccess"
    />
    
    <!-- 用户详情模态框 -->
    <UserDetailModal
      v-model:visible="showDetailModal"
      :user="currentUser"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { PlusOutlined, EditOutlined, EyeOutlined, KeyOutlined } from '@ant-design/icons-vue'
import { DataTable, StatusTag, LanguageTag, type TableAction } from '@/shared/components'
import { formatDateTime } from '@/shared/utils'
import { useUserStore } from '@/stores'
import { getUsers, getUserStats, resetUserPassword, type UserInfo, type UserStatsResponse } from '../api'
import { USER_RESET_DEFAULT_PASSWORD } from '../constants'
import { CreateUserModal, EditUserModal, UserDetailModal } from '../components'
import { useI18n } from 'vue-i18n'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'

const { t } = useI18n()

const userStore = useUserStore()

const loading = ref(false)
const users = ref<UserInfo[]>([])
const stats = ref<UserStatsResponse>({
  total_users: 0,
  active_users: 0,
  normal_users: 0,
  restricted_users: 0,
  admin_users: 0
})

const showCreateModal = ref(false)
const showEditModal = ref(false)
const showDetailModal = ref(false)
const currentUser = ref<UserInfo | null>(null)

const searchForm = reactive({
  search: '',
  status: undefined
})

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total: number) => t('common.totalRecords', { total })
})

const columns = computed(() => [
  {
    title: t('user.username'),
    dataIndex: 'username',
    key: 'username'
  },
  {
    title: t('user.name'),
    dataIndex: 'name',
    key: 'name'
  },
  {
    title: t('user.email'),
    dataIndex: 'email',
    key: 'email'
  },
  {
    title: t('user.roles') || '角色',
    dataIndex: 'roles',
    key: 'roles'
  },
  {
    title: t('common.status'),
    dataIndex: 'status',
    key: 'status'
  },
  {
    title: t('user.language'),
    dataIndex: 'language',
    key: 'language'
  },
  {
    title: t('user.createTimeLabel'),
    dataIndex: 'created_at',
    key: 'created_at'
  }
])

const actions: TableAction[] = [
  {
    key: 'view',
    label: t('common.view'),
    type: 'link',
    icon: EyeOutlined
  },
  {
    key: 'edit',
    label: t('common.edit'),
    type: 'link',
    icon: EditOutlined,
    visible: () => userStore.canManageUsers
  },
  {
    key: 'resetPassword',
    label: t('user.resetPassword'),
    type: 'link',
    icon: KeyOutlined,
    visible: () => userStore.canManageUsers
  }
]

const loadUsers = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  try {
    if (manageLoading) {
      loading.value = true
    }

    const params = {
      skip: (pagination.current - 1) * pagination.pageSize,
      limit: pagination.pageSize,
      ...searchForm
    }

    const response = await getUsers(params, batchCtx?.requestConfig)
    users.value = response.items || []
    pagination.total = response.total || users.value.length
  } catch (error) {
    if (!batchCtx) {
      console.error('Failed to load users:', error)
    }
    throw error
  } finally {
    if (manageLoading) {
      loading.value = false
    }
  }
}

const loadStats = async (batchCtx?: BatchContext) => {
  try {
    const response = await getUserStats(batchCtx?.requestConfig)
    stats.value = response
  } catch (error) {
    if (!batchCtx) {
      console.error('Failed to load stats:', error)
    }
    throw error
  }
}

const refreshPageData = async () => {
  await runApiBatch([
    (ctx) => loadUsers(ctx),
    (ctx) => loadStats(ctx),
  ])
}

const handleSearch = () => {
  pagination.current = 1
  loadUsers()
}

const handleReset = () => {
  searchForm.search = ''
  searchForm.status = undefined
  pagination.current = 1
  loadUsers()
}

const handleTableChange = (pag: any) => {
  pagination.current = pag.current || pagination.current
  pagination.pageSize = pag.pageSize || pagination.pageSize
  loadUsers()
}

const handleAction = (action: string, record: UserInfo) => {
  currentUser.value = record
  
  switch (action) {
    case 'view':
      showDetailModal.value = true
      break
    case 'edit':
      showEditModal.value = true
      break
    case 'resetPassword':
      handleResetPassword(record)
      break
  }
}

const handleResetPassword = (user: UserInfo) => {
  Modal.confirm({
    title: t('user.resetUserPassword'),
    content: t('user.resetPasswordConfirm', {
      name: user.name,
      password: USER_RESET_DEFAULT_PASSWORD
    }),
    okText: t('user.confirmReset'),
    onOk: async () => {
      try {
        await resetUserPassword(user.id, USER_RESET_DEFAULT_PASSWORD)
        message.success(
          t('user.passwordResetSuccess', { password: USER_RESET_DEFAULT_PASSWORD })
        )
      } catch (error) {
        console.error('Failed to reset password:', error)
      }
    }
  })
}

const handleCreateSuccess = () => {
  showCreateModal.value = false
  refreshPageData()
}

const handleEditSuccess = () => {
  showEditModal.value = false
  refreshPageData()
}

onMounted(() => {
  refreshPageData()
})
</script>

<style scoped lang="less">
.user-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.stats-row {
  margin-bottom: 24px;
}

.search-card {
  margin-bottom: 24px;
}
</style>

